<template>
  <section
    class="bg-monthly-bonus-bg bg-cover bg-fixed bg-top bg-no-repeat h-auto bg-[#00284F]"
  >
    <div class="max-w-[1200px] mx-auto px-4 lg:px-8">
      <div class="flex flex-col items-right pt-28 pb-20 md:pt-36 lg:pt-40 font-oxanium">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-5xl sm:text-6xl font-extrabold font-golos-text">
            <span class="text-[#FDB913] ml-2 uppercase">{{ bonusTitle }}</span>
            MONTHLY BONUS
          </h1>
          <p class="text-lg sm:text-2xl font-montserrat">CLAIM YOUR MONTHLY BONUS</p>
        </div>

        <!-- Loading Spinner -->
        <div
          v-if="loading"
          class="my-10 text-white font-bold text-base font-orbitron h-[300px] mt-[200px]"
        >
          <span
            class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12 block mx-auto"
          ></span>
        </div>

        <div v-if="!loading">
          <div class="flex flex-col justify-center items-center mt-10">
            <p class="text-lg sm:text-2xl font-montserrat mb-4">
              <span v-if="!mainCountdownEnded">TIME REMAINING TO CLAIM BONUS</span>
              <span
                class="text-[#FF2626] font-extrabold uppercase tracking-wide text-base sm:text-xl animate-pulse drop-shadow-[0_0_4px_#FF2626]"
                v-else-if="canClaimGracePeriod"
                >FINAL CHANCE! CLAIM YOUR MONTHLY BONUS BEFORE TIME RUNS OUT:</span
              >
              <span v-else>{{ graceMessage }}</span>
            </p>

            <!-- Countdown Timer or Grace Timer -->
            <div class="flex text-white items-center mt-4">
              <div
                v-for="(unit, i) in mainCountdownEnded ? gracePeriodUnits : timeUnits"
                :key="i"
                class="flex items-center relative"
              >
                <!-- Timer Box -->
                <div class="flex flex-col items-center">
                  <div
                    class="bg-black text-2xl sm:text-6xl font-bold px-4 sm:px-6 py-4 rounded-md min-w-[40px] sm:min-w-[80px] text-center"
                  >
                    {{ unit.value }}
                  </div>
                  <span class="mt-2 text-sm sm:text-base tracking-wide uppercase">
                    {{ unit.label }}
                  </span>
                </div>

                <!-- Connector -->
                <div
                  v-if="
                    i <
                    (mainCountdownEnded ? gracePeriodUnits.length : timeUnits.length) - 1
                  "
                  class="relative h-[60px] w-[16px] mx-2 flex items-center justify-center"
                >
                  <div class="w-[6px] h-full rounded-md relative">
                    <div
                      class="absolute top-[5%] left-1/2 -translate-x-1/2 w-[6px] h-[6px] bg-white rounded-full"
                    ></div>
                    <div
                      class="absolute bottom-[45%] left-1/2 -translate-x-1/2 w-[6px] h-[6px] bg-white rounded-full"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Monthly Reward Amount Display -->
            <div
              class="mt-10 w-full max-w-[920px] bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl px-6 py-5 flex flex-col gap-4 shadow-lg font-orbitron"
            >
              <!-- Top: Label + Button -->
              <div class="w-full flex flex-col sm:flex-row justify-between items-center">
                <!-- Label -->
                <div
                  class="text-white text-lg sm:text-base font-bold flex items-center gap-1"
                >
                  <span class="hidden md:flex text-[#EEAF0E] text-[80px] mb-[2px]"
                    >•</span
                  >
                  <span class="text-white text-sm sm:text-base">Current Monthly</span>
                  <span class="text-[#EEAF0E] ml-1 text-sm sm:text-base"
                    >Reward Amount</span
                  >
                </div>

                <!-- Claim Now -->
                <button
                  v-if="!isClaimed && (!mainCountdownEnded || canClaimGracePeriod)"
                  @click="openDiscordModal"
                  class="bg-[#1992F4] hover:bg-[#0c7bd8] transition text-white font-semibold text-sm px-5 py-3 w-full sm:w-[200px] mt-4 sm:mt-0 rounded-md flex justify-center items-center gap-2 whitespace-nowrap"
                >
                  <img
                    src="@/assets/tabler-icon-moneybag.png"
                    alt="Moneybag Icon"
                    class="w-5 h-5 object-contain"
                  />
                  Claim Now
                </button>

                <!-- Already Claimed -->
                <span
                  v-else-if="isClaimed"
                  class="text-[#7A8CA4] font-semibold text-sm px-5 py-3 w-full sm:w-[200px] mt-4 sm:mt-0 rounded-md text-center border border-[#7A8CA4]"
                >
                  Already Claimed
                </span>

                <span
                  v-else
                  class="text-[#7A8CA4] font-semibold text-sm px-5 py-3 w-full sm:w-[200px] mt-4 sm:mt-0 rounded-md text-center border border-[#7A8CA4]"
                >
                  Claim Period Ended
                </span>
              </div>

              <!-- Bottom: Digits Display -->
              <div
                class="flex items-center px-4 py-2 bg-[#FDB913] rounded-[6px] relative overflow-x-auto whitespace-nowrap"
                style="clip-path: polygon(16px 0%, 100% 0%, 100% 100%, 16px 100%, 0% 50%)"
              >
                <!-- Digits -->
                <div
                  class="flex justify-center items-end w-auto sm:w-full lg:w-auto xl:w-full gap-x-1"
                >
                  <!-- Dollar Sign -->
                  <div
                    class="text-black text-[32px] sm:text-[30px] md:text-[40px] lg:text-[54px] font-black mr-3 pl-1 sm:mb-2 leading-none"
                  >
                    $
                  </div>

                  <div
                    v-for="(char, i) in rewardFormatted"
                    :key="i"
                    :class="[
                      'rounded-[4px] text-center font-black leading-none',
                      char === ',' || char === '.'
                        ? 'text-black text-[24px] sm:text-[38px] mx-[2px] sm:mx-[4px]'
                        : 'px-[4px] sm:px-[6px] py-[3px] sm:py-[4px] min-w-[20px] sm:min-w-[28px] md:min-w-[36px] text-[24px] sm:text-[30px] md:text-[40px] lg:text-[56px]',
                      i < firstActiveDigitIndex && char !== ',' && char !== '.'
                        ? 'bg-[#1A1A1A] text-[#444]'
                        : char !== ',' && char !== '.'
                        ? 'bg-[#1A1A1A] text-white'
                        : '',
                    ]"
                  >
                    {{ char }}
                  </div>
                </div>
              </div>

              <div
                class="flex sm:hidden justify-center w-full max-w-7xl items-center space-x-4 text-white font-bold mt-[20px]"
              >
                <!-- Background Container -->
                <div
                  class="relative w-16 h-8 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden"
                >
                  <!-- Animated Circle -->
                  <div
                    class="absolute top-1/2 left-0 transform w-4 h-4 rounded-full bg-white animate-scroll-dot"
                  ></div>
                </div>

                <span class="font-galos">Scroll To Right</span>
              </div>
            </div>

            <!-- Reward History Section -->
            <div
              class="mt-8 w-full max-w-[920px] bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl px-3 sm:px-6 py-5 shadow-lg text-white font-montserrat"
            >
              <!-- Header -->
              <div class="flex items-center justify-center sm:justify-start mb-4 gap-2">
                <span class="text-[#FDB913] text-[50px] mb-2 font-semibold">•</span>
                <h2 class="text-white text-lg sm:text-xl font-bold">
                  Rewards <span class="text-[#FDB913]">History</span>
                </h2>
              </div>

              <!-- Table -->
              <div class="w-full">
                <!-- Table Header -->
                <div
                  class="grid grid-cols-3 text-sm sm:text-base font-semibold text-[#cccccc] bg-[#2A2A2A] rounded-md px-4 py-3"
                >
                  <div class="text-left">Month</div>
                  <div class="text-center">Reward Amount</div>
                  <div class="text-right">Status</div>
                </div>

                <!-- Dynamic Rows -->
                <div v-if="rewardHistory.length">
                  <div
                    v-for="(item, index) in rewardHistory"
                    :key="index"
                    class="grid grid-cols-3 items-center text-white px-4 py-4 mt-2 rounded-md font-semibold"
                    :class="index % 2 === 0 ? 'bg-[#313B44]' : 'bg-[#2C2C2C]'"
                  >
                    <div class="text-left text-white font-bold">{{ item.month }}</div>
                    <div class="text-center text-[#FDB913] font-bold">
                      {{ item.reward }}
                    </div>

                    <!-- Status Column -->
                    <div class="flex justify-end items-center w-full text-right">
                      <!-- Claimed -->
                      <span
                        v-if="item.status === 'Claimed'"
                        class="text-[#7A8CA4] font-semibold text-xs sm:text-sm px-2 sm:px-5 py-3 w-[100px] sm:w-[120px] mt-2 sm:mt-0 rounded-md text-center border border-[#7A8CA4] inline-block"
                      >
                        Claimed
                      </span>

                      <!-- Unclaimed (Expired) -->
                      <span
                        v-else-if="item.status === 'Unclaimed'"
                        class="text-[#FF2626] font-semibold text-xs sm:text-sm px-2 sm:px-5 py-3 w-[100px] sm:w-[120px] mt-2 sm:mt-0 rounded-md text-center border border-[#FF2626] inline-block"
                      >
                        Unclaimed
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Fallback Message -->
                <div
                  v-else
                  class="text-center text-[#cccccc] font-semibold py-6 text-sm sm:text-base"
                >
                  No rewards yet for this monthly bonus type.
                </div>
              </div>
            </div>

            <!-- How to Claim Section -->
            <div
              class="mt-8 w-full max-w-[920px] bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl px-3 sm:px-6 py-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 shadow-lg text-white font-montserrat"
            >
              <!-- Left Side: Text -->
              <div class="flex-1">
                <!-- Header row: dot + heading -->
                <div class="flex items-center gap-2 mb-3">
                  <div
                    class="flex items-center justify-center w-[24px] h-[24px] sm:w-[28px] sm:h-[28px]"
                  >
                    <span
                      class="hidden md:flex text-[#EEAF0E] text-[80px] mb-3 leading-none"
                      >•</span
                    >
                  </div>
                  <h2 class="text-lg sm:text-xl font-bold whitespace-nowrap">
                    How to Claim <span class="text-[#EEAF0E]">Monthly Bonus</span>
                  </h2>
                </div>

                <!-- List -->
                <ul class="list-disc list-inside text-sm sm:text-base space-y-2 ml-4">
                  <li>
                    You can claim your Monthly Bonus once per month at the end of each
                    month.
                  </li>
                  <li>
                    The total amount is determined by your Deposits, Withdrawls & Wager
                    during the month.
                  </li>
                  <li>
                    Monthly Bonus can only be claimed by Metaspins & Dailyspins
                    Affiliates.
                  </li>
                  <li>
                    The Monthly Bonus will only be claimable for up to 72 hours after the
                    timer ends.
                  </li>
                  <li class="text-red-500">
                    The Leaderboard Prizes will greatly affect your FINAL MONTHLY BONUS.
                  </li>
                  <li>Claim your Monthly Bonus by clicking Claim Now!</li>
                </ul>
              </div>

              <!-- Right Side: Image -->
              <div class="hidden sm:flex flex-shrink-0">
                <img
                  src="@/assets/treasure-card.png"
                  alt="Treasure Chest"
                  class="w-[80px] h-auto sm:w-[220px] lg:w-[200px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Discord Join Modal -->
    <transition name="fade">
      <div
        v-if="showDiscordModal"
        class="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4"
      >
        <div
          class="bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl shadow-2xl w-full max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] min-h-[200px] sm:min-h-[260px] lg:min-h-[260px] p-6 sm:p-10 text-center relative flex flex-col justify-center items-center"
        >
          <!-- Close Button -->
          <button
            @click="showDiscordModal = false"
            class="absolute top-3 right-4 text-white text-4xl font-bold hover:text-gray-300"
          >
            ×
          </button>

          <!-- Message -->
          <p
            class="text-white text-xl sm:text-2xl font-semibold leading-snug mb-6 font-golos"
          >
            Please Join our Discord Channel to Claim your
            <span class="text-[#FDB913] font-bold">Monthly Bonus</span>
          </p>

          <!-- Discord Button -->
          <a
            href="https://discord.gg/ballyboy"
            target="_blank"
            class="bg-[#5865F2] hover:bg-[#4752c4] text-white font-bold font-golos py-4 px-8 rounded-lg text-base sm:text-lg inline-flex items-center justify-center gap-3"
          >
            <img src="@/assets/discord-white.png" class="w-6 h-6" alt="Discord Icon" />
            Join Discord Now!
          </a>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import axios from "axios";
import store from "@/store";
import { defineComponent } from "vue";

export default defineComponent({
  data() {
    return {
      loading: false,
      userToken: null,
      timeUnits: [
        { label: "Days", value: "00" },
        { label: "Hours", value: "00" },
        { label: "Minutes", value: "00" },
        { label: "Seconds", value: "00" },
      ],
      interval: null,
      gracePeriodUnits: [
        { label: "Days", value: "00" },
        { label: "Hours", value: "00" },
        { label: "Minutes", value: "00" },
        { label: "Seconds", value: "00" },
      ],
      canClaimGracePeriod: false,
      graceMessage: "",
      isMobileView: window.innerWidth < 640,
      rewardFormatted: "000,000,000.00".split(""),
      firstActiveDigitIndex: 0,
      rewardHistory: [],
      isClaimed: false,
      showDiscordModal: false,
      mainCountdownEnded: false,
    };
  },
  watch: {
    "$route.query.type"() {
      this.fetchMonthlyBonus();
    },
  },
  computed: {
    bonusTitle() {
      const type = this.$route.query.type || "metaspins";
      return type;
    },
  },
  methods: {
    async fetchMonthlyBonus() {
      this.loading = true;
      try {
        const token =
          store.state.user?.accessToken || localStorage.getItem("ballyboy-access-token");
        if (!token) {
          this.setRewardToZero();
          return;
        }

        const type = this.$route.query.type || "metaspins";

        const res = await axios.get(
          process.env.VUE_APP_BASE_API + "/api/user/monthly-bonus",
          {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const bonuses = res.data?.monthlyBonuses || [];
        const current = bonuses.find((b) => b.leaderboardType === type);

        clearInterval(this.interval);

        if (
          current?.data &&
          Object.prototype.hasOwnProperty.call(current.data, "isClaimed")
        ) {
          this.isClaimed = !!current.data.isClaimed;

          if (!isNaN(parseFloat(current.data.bonus))) {
            const reward = parseFloat(current.data.bonus).toFixed(2);
            const rewardStr = Number(reward).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });

            let [integerPart, decimalPart] = rewardStr.split(".");
            integerPart = integerPart.replace(/,/g, "").padStart(9, "0");
            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

            this.rewardFormatted = (integerPart + "." + decimalPart).split("");

            this.firstActiveDigitIndex = this.rewardFormatted.findIndex(
              (c) => c !== "0" && c !== "," && c !== "."
            );
            if (this.firstActiveDigitIndex === -1) {
              this.firstActiveDigitIndex = this.rewardFormatted.indexOf(".");
              if (this.firstActiveDigitIndex !== -1) {
                this.firstActiveDigitIndex--;
              } else {
                this.firstActiveDigitIndex = this.rewardFormatted.length - 3;
              }
            }
          } else {
            this.setRewardToZero();
          }

          if (current?.endDate) {
            this.updateCountdown(current.endDate);
            this.interval = setInterval(
              () => this.updateCountdown(current.endDate),
              1000
            );
            this.graceUntil = new Date(
              new Date(current.endDate).getTime() + 72 * 60 * 60 * 1000
            );
            this.startGraceCountdown();
          }
        } else {
          this.setRewardToZero();
        }

        this.rewardHistory = bonuses
          .filter((b) => {
            const isSameType = b.leaderboardType === type;
            if (!isSameType) return false;

            const bonusDate = new Date(b.endDate);
            const graceUntil = new Date(bonusDate.getTime() + 72 * 60 * 60 * 1000);
            const graceEnded = new Date() > graceUntil;
            const isClaimed = !!b.data?.isClaimed;
            const hasBonus = true;

            const isCurrent = b._id === current?._id;

            return hasBonus && (graceEnded || (isClaimed && !isCurrent));
          })

          .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))
          .map((entry) => {
            const date = new Date(entry.endDate);
            const month = date.toLocaleString("default", { month: "long" });
            const rawBonus = entry.data?.bonus;
            const bonusValue = isNaN(parseFloat(rawBonus)) ? 0 : parseFloat(rawBonus);
            const formatted = `$ ${bonusValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;

            return {
              month,
              reward: formatted,
              status: entry.data?.isClaimed ? "Claimed" : "Unclaimed",
            };
          });
      } catch (err) {
        console.error("Error fetching monthly bonus:", err);
        this.setRewardToZero();
      } finally {
        this.loading = false;
      }
    },

    startGraceCountdown() {
      const update = () => {
        const nowUtc = new Date(new Date().toISOString());
        const diff = this.graceUntil.getTime() - nowUtc.getTime();

        this.canClaimGracePeriod = this.mainCountdownEnded && diff > 0;

        if (this.canClaimGracePeriod) {
          let remaining = diff;
          const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
          remaining %= 1000 * 60 * 60 * 24;
          const hours = Math.floor(remaining / (1000 * 60 * 60));
          remaining %= 1000 * 60 * 60;
          const minutes = Math.floor(remaining / (1000 * 60));
          remaining %= 1000 * 60;
          const seconds = Math.floor(remaining / 1000);

          this.gracePeriodUnits = [
            { label: "Days", value: String(days).padStart(2, "0") },
            { label: "Hours", value: String(hours).padStart(2, "0") },
            { label: "Minutes", value: String(minutes).padStart(2, "0") },
            { label: "Seconds", value: String(seconds).padStart(2, "0") },
          ];
        } else if (diff <= 0) {
          this.canClaimGracePeriod = false;
          this.graceMessage =
            "Bonus claim period has expired. Waiting for next monthly bonus.";
          this.gracePeriodUnits = [
            { label: "Days", value: "00" },
            { label: "Hours", value: "00" },
            { label: "Minutes", value: "00" },
            { label: "Seconds", value: "00" },
          ];
          clearInterval(this.graceInterval);

          if (!this.isClaimed) {
            const expiredMonth = new Date(
              this.graceUntil.getTime() - 72 * 3600 * 1000
            ).toLocaleString("default", { month: "long" });

            const exists = this.rewardHistory.some((item) => item.month === expiredMonth);

            if (!exists) {
              this.rewardHistory.unshift({
                month: expiredMonth,
                reward: `$ 0.00`,
                status: "Unclaimed",
              });
            }
          }

          this.setRewardToZero();
        }
      };

      update();
      this.graceInterval = setInterval(update, 1000);
    },

    setRewardToZero() {
      this.rewardFormatted = "000,000,000.00".split("");
      this.firstActiveDigitIndex = 0;
      this.setCountdownToZero();
      this.isClaimed = false;
    },

    setCountdownToZero() {
      this.timeUnits = [
        { label: "Days", value: "00" },
        { label: "Hours", value: "00" },
        { label: "Minutes", value: "00" },
        { label: "Seconds", value: "00" },
      ];
    },

    updateCountdown(endDate) {
      const now = new Date();
      const target = new Date(endDate);
      let diff = target - now;

      if (diff <= 0) {
        this.setCountdownToZero();
        clearInterval(this.interval);
        this.mainCountdownEnded = true;
        return;
      } else {
        this.mainCountdownEnded = false;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      diff %= 1000 * 60 * 60 * 24;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      diff %= 1000 * 60 * 60;
      const minutes = Math.floor(diff / (1000 * 60));
      diff %= 1000 * 60;
      const seconds = Math.floor(diff / 1000);

      this.timeUnits[0].value = String(days).padStart(2, "0");
      this.timeUnits[1].value = String(hours).padStart(2, "0");
      this.timeUnits[2].value = String(minutes).padStart(2, "0");
      this.timeUnits[3].value = String(seconds).padStart(2, "0");
    },

    handleResize() {
      this.isMobileView = window.innerWidth < 640;
    },
    openDiscordModal() {
      this.showDiscordModal = true;
    },
  },
  mounted() {
    this.fetchMonthlyBonus();
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeUnmount() {
    clearInterval(this.interval);
    window.removeEventListener("resize", this.handleResize);
  },
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
